/**
 * CreateDocumentDatabaseModal Component
 *
 * Modal for creating a new document database.
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { useCreateDocumentDatabase } from '../hooks/useCreateDocumentDatabase';
import { createDocumentDBFormSchema, type CreateDocumentDBFormData } from '../../../lib/validators/document-database';

/**
 * Generate document database code from name
 * Converts "User Profiles" to "user-profiles"
 */
const generateCodeFromName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
};

interface CreateDocumentDatabaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}

/**
 * CreateDocumentDatabaseModal component
 */
export const CreateDocumentDatabaseModal: React.FC<CreateDocumentDatabaseModalProps> = ({
  isOpen,
  onClose,
  tenantId,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateDocumentDBFormData>({
    resolver: zodResolver(createDocumentDBFormSchema),
    defaultValues: {
      documentDBCode: '',
      documentDBName: '',
      description: '',
      schemaVersion: '',
    },
  });

  const watchName = watch('documentDBName');

  // Auto-generate document database code from name
  useEffect(() => {
    if (autoGenerateCode && watchName) {
      const generatedCode = generateCodeFromName(watchName);
      setValue('documentDBCode', generatedCode);
    }
  }, [watchName, autoGenerateCode, setValue]);

  const { mutate: createDocumentDatabase, isPending } = useCreateDocumentDatabase(tenantId, {
    onSuccess: () => {
      setSuccessMessage('Document database created successfully!');
      setErrorMessage('');
      setTimeout(() => {
        reset();
        onClose();
        setSuccessMessage('');
      }, 1500);
    },
    onError: (error) => {
      setErrorMessage(error);
      setSuccessMessage('');
    },
  });

  const onSubmit = (data: CreateDocumentDBFormData) => {
    setErrorMessage('');
    setSuccessMessage('');

    // Create document database DTO
    const documentDBData = {
      documentDBCode: data.documentDBCode,
      documentDBName: data.documentDBName,
      description: data.description || undefined,
      schemaVersion: data.schemaVersion || undefined,
    };

    createDocumentDatabase(documentDBData);
  };

  const handleClose = () => {
    if (!isPending) {
      reset();
      setErrorMessage('');
      setSuccessMessage('');
      setAutoGenerateCode(true);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Create Document Database</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Info Note */}
          <Alert variant="info">
            Document databases store JSON documents that your applications can read, write, and query.
          </Alert>

          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Success Message */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* DocumentDB Name */}
          <FormField label="Database Name" required error={errors.documentDBName?.message}>
            <Input
              {...register('documentDBName')}
              placeholder="e.g., User Profiles"
              error={!!errors.documentDBName}
            />
          </FormField>

          {/* DocumentDB Code */}
          <FormField label="Database Code" required error={errors.documentDBCode?.message}>
            <Input
              {...register('documentDBCode')}
              placeholder="e.g., user-profiles"
              error={!!errors.documentDBCode}
              disabled={autoGenerateCode}
            />
            <div className="mt-2 flex items-center">
              <Checkbox
                id="autoGenerateDocumentDBCode"
                checked={autoGenerateCode}
                onChange={(e) => setAutoGenerateCode(e.target.checked)}
              />
              <label htmlFor="autoGenerateDocumentDBCode" className="ml-2 text-sm text-gray-600">
                Auto-generate code from name
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Code is immutable after creation. Use lowercase letters, numbers, and hyphens.
            </p>
          </FormField>

          {/* Description */}
          <FormField label="Description" error={errors.description?.message}>
            <Textarea
              {...register('description')}
              placeholder="Describe the purpose of this document database..."
              rows={3}
              error={!!errors.description}
            />
          </FormField>

          {/* Schema Version */}
          <FormField label="Schema Version" error={errors.schemaVersion?.message}>
            <Input
              {...register('schemaVersion')}
              placeholder="e.g., 1.0.0"
              error={!!errors.schemaVersion}
            />
            <p className="mt-1 text-xs text-gray-500">
              Optional version identifier for your document schema.
            </p>
          </FormField>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending}>
              Create Database
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
